import { supabase, unwrap, DbError } from '../supabase';
import { normalizeBillingAddress } from '../billing-address';
import { sleep } from '../utils';
import { countryName } from '@shared/countries';
import { detectSignupCountry } from '../signup-country';
import { TRIAL, planAt, type PlanCapacity, type ScheduledPlan } from '@shared/pricing';
import type { AuthUser, BillingAddress, Org } from '../types';

const num = (v: unknown, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

function mapBillingAddress(row: Record<string, unknown>): BillingAddress {
  const raw = row.billing_address;
  const obj = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const str = (v: unknown) => (v === null || v === undefined ? '' : String(v));
  return {
    address: str(typeof raw === 'string' ? raw : obj.address),
    city: str(obj.city ?? row.city),
    state: str(obj.state ?? row.state),
    zip_code: str(obj.zip_code ?? row.zip_code),
    country: str(obj.country ?? row.country),
    gst_number: str(obj.gst_number ?? row.gst_number),
    billing_name: str(obj.billing_name ?? row.name),
    gst_org_name: str(obj.gst_org_name ?? ((obj.gst_number ?? row.gst_number) ? row.name : '')),
  };
}

export function mapOrg(row: Record<string, unknown>): Org {
  const current = planAt({
    planId: (row.plan_id as string) || null,
    planExpiry: (row.plan_expiry_date as string) || null,
    allowedUsers: num(row.allowed_users, TRIAL.users),
    allowedStorageGb: num(row.allowed_storage_gb, TRIAL.storageGb),
    hasWebhookAccess: Boolean(row.has_webhook_access),
    renewalPlan: (row.renewal_plan as PlanCapacity) || null,
    scheduledPlan: (row.scheduled_plan as ScheduledPlan) || null,
  });
  return {
    ...current,
    id: String(row.org_id),
    name: String(row.name || ''),
    creatorId: (row.creator_id as string) || null,
    createdAt: (row.created_at as string) || null,
    onboardingCompleted: row.onboarding_completed === true,
    onboardingCompletedAt: (row.onboarding_completed_at as string) || null,
    // 0 is a real value (e.g. a plan without storage) — don't default it away.
    storageUsedBytes: num(row.storage_used_bytes),
    lastStorageCheck: (row.last_storage_check as string) || (row.updated_at as string) || null,
    transcriptionEnabled: Boolean(row.transcription_enabled),
    autoTranscribe: Boolean(row.auto_transcribe),
    transcriptionCredits: num(row.transcription_credits),
    billingAddress: mapBillingAddress(row),
  };
}

async function selectOrgById(orgId: string) {
  const res = await supabase.from('organizations').select('*').eq('org_id', orgId).maybeSingle();
  if (res.error) throw new DbError(res.error.message, res.error.code);
  return res.data as Record<string, unknown> | null;
}

async function selectOrgByCreator(userId: string) {
  const res = await supabase
    .from('organizations')
    .select('*')
    .eq('creator_id', userId)
    .order('created_at', { ascending: true })
    .limit(1);
  if (res.error) throw new DbError(res.error.message, res.error.code);
  return (res.data?.[0] as Record<string, unknown>) || null;
}

/** Fill an unset country once, including Google signups provisioned by the auth trigger. */
async function withSignupCountry(row: Record<string, unknown>, user: AuthUser): Promise<Org> {
  const org = mapOrg(row);
  if (countryName(org.billingAddress.country) || org.creatorId !== user.id) return org;
  const country =
    countryName(typeof user.metadata.country === 'string' ? user.metadata.country : null) ??
    (await detectSignupCountry());
  if (!country) return org;

  // Compare the snapshot timestamp so a concurrent address save always wins.
  let update = supabase
    .from('organizations')
    .update({
      billing_address: { ...org.billingAddress, country },
      updated_at: new Date().toISOString(),
    })
    .eq('org_id', org.id)
    .eq('creator_id', user.id);
  update = row.updated_at ? update.eq('updated_at', row.updated_at) : update.is('updated_at', null);
  const result = await update.select('*').maybeSingle();
  if (result.error) return org; // Manual country selection remains available if saving the hint fails.
  if (result.data) return mapOrg(result.data as Record<string, unknown>);
  const latest = await selectOrgById(org.id);
  return latest ? mapOrg(latest) : org;
}

/**
 * Find the signed-in user's organisation.
 *
 * Normal path: the auth.users trigger created it from `user_metadata.org_id`.
 * If it is not there yet (trigger still running, or an older Google account
 * with no org id), look it up by creator, wait briefly, and only then create
 * it ourselves — by a deterministic id, so a retry can never make a second one.
 */
export async function resolveOrg(user: AuthUser): Promise<Org> {
  const metaOrgId = typeof user.metadata.org_id === 'string' ? user.metadata.org_id : '';

  for (let attempt = 0; attempt < 3; attempt++) {
    if (metaOrgId) {
      const row = await selectOrgById(metaOrgId);
      if (row) return withSignupCountry(row, user);
    }
    const byCreator = await selectOrgByCreator(user.id);
    if (byCreator) {
      if (byCreator.org_id !== metaOrgId) {
        supabase.auth.updateUser({ data: { org_id: byCreator.org_id } }).catch(() => undefined);
      }
      return withSignupCountry(byCreator, user);
    }
    // Brand new signups: give the provisioning trigger a moment.
    const isNew = user.createdAt ? Date.now() - new Date(user.createdAt).getTime() < 5 * 60_000 : false;
    if (!isNew || attempt === 2) break;
    await sleep(1200);
  }

  const orgId = metaOrgId || `org_${user.id.replace(/-/g, '').slice(0, 15)}`;
  // A stale access token must never recreate the workspace of a deleted account.
  const verified = await supabase.auth.getUser();
  if (verified.error) throw new DbError(verified.error.message, verified.error.code);
  if (!verified.data.user || verified.data.user.id !== user.id)
    throw new DbError('Your account no longer exists. Please sign in again.', 'user_not_found');
  const company = String(user.metadata.company_name || '').trim();
  const name = company || (user.name ? `${user.name}'s Organization` : 'My Organization');

  if (metaOrgId !== orgId || !user.metadata.company_name) {
    await supabase.auth.updateUser({ data: { org_id: orgId, company_name: name } });
    await supabase.auth.refreshSession();
  }

  const insert = await supabase
    .from('organizations')
    .insert({
      org_id: orgId,
      name,
      creator_id: user.id,
      allowed_users: TRIAL.users,
      allowed_storage_gb: TRIAL.storageGb,
      transcription_credits: TRIAL.aiCredits,
      storage_used_bytes: 0,
      plan_id: 'trial',
      has_webhook_access: TRIAL.integrations,
      plan_expiry_date: new Date(Date.now() + TRIAL.days * 86_400_000).toISOString(),
    })
    .select()
    .maybeSingle();

  if (insert.data) return withSignupCountry(insert.data as Record<string, unknown>, user);
  if (insert.error && insert.error.code !== '23505') {
    throw new DbError(`We couldn't set up your workspace: ${insert.error.message}`, insert.error.code);
  }
  const existing = await selectOrgById(orgId);
  if (existing) return withSignupCountry(existing, user);
  throw new DbError("We couldn't find your workspace. Please contact support.");
}

export async function getOrg(orgId: string): Promise<Org> {
  const row = await selectOrgById(orgId);
  if (!row) throw new DbError('Workspace not found');
  return mapOrg(row);
}

const onboardingWrites = new Map<string, Promise<Org>>();
/** Persist completion once; concurrent setup observers share the same write. */
export function completeOnboarding(orgId: string): Promise<Org> {
  const pending = onboardingWrites.get(orgId);
  if (pending) return pending;
  const write = (async () => {
    const result = await supabase
      .from('organizations')
      .update({ onboarding_completed: true })
      .eq('org_id', orgId)
      .eq('onboarding_completed', false)
      .select('*')
      .maybeSingle();
    if (result.error) throw new DbError(result.error.message, result.error.code);
    const org = result.data ? mapOrg(result.data) : await getOrg(orgId);
    if (!org.onboardingCompleted) throw new Error('Could not save setup completion. Please try again.');
    return org;
  })().finally(() => onboardingWrites.delete(orgId));
  onboardingWrites.set(orgId, write);
  return write;
}

/** True while the org still carries a placeholder name from provisioning. */
export function hasPlaceholderName(name: string | null | undefined): boolean {
  const n = (name || '').trim();
  return (
    !n ||
    /^org_/i.test(n) ||
    /^my (organi[sz]ation|workspace|company)$/i.test(n) ||
    /['’]s organi[sz]ation$/i.test(n)
  );
}

export async function updateOrgDetails(
  orgId: string,
  patch: { name?: string; billingAddress?: BillingAddress },
): Promise<void> {
  const update: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (patch.name !== undefined) update.name = patch.name.trim();
  if (patch.billingAddress) {
    const address = normalizeBillingAddress(patch.billingAddress);
    if (!address.country) throw new Error('Choose your billing country.');
    update.billing_address = address;
  }
  unwrap(await supabase.from('organizations').update(update).eq('org_id', orgId));
  if (patch.name !== undefined) {
    await supabase.auth.updateUser({ data: { company_name: patch.name.trim() } });
  }
}

export async function updateAiSettings(
  orgId: string,
  patch: { transcriptionEnabled?: boolean; autoTranscribe?: boolean },
): Promise<void> {
  const update: Record<string, unknown> = {};
  if (patch.transcriptionEnabled !== undefined) update.transcription_enabled = patch.transcriptionEnabled;
  if (patch.autoTranscribe !== undefined) update.auto_transcribe = patch.autoTranscribe;
  unwrap(await supabase.from('organizations').update(update).eq('org_id', orgId));
}

export async function recalculateStorage(
  orgId: string,
): Promise<{ storageUsedBytes: number; lastStorageCheck: string | null }> {
  const data = unwrap(await supabase.rpc('recalculate_org_storage', { p_org_id: orgId })) as Record<
    string,
    unknown
  > | null;
  return {
    storageUsedBytes: num(data?.storage_used_bytes),
    lastStorageCheck: (data?.last_storage_check as string) || new Date().toISOString(),
  };
}
